import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chat from './components/Chat';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { initNotifications, scheduleToday, requestNotificationPermission } from './utils/notifications';
import './styles/mcu.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TIME_OPTS = { timeZone: 'Asia/Dhaka', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' };

// Shared clock state — single interval for all Clock instances
let _clockListeners = new Set();
let _clockTime = '';
let _clockInterval = null;
function _startClock() {
  if (_clockInterval) return;
  const tick = () => { _clockTime = new Date().toLocaleTimeString('en-US', TIME_OPTS); _clockListeners.forEach(fn => fn(_clockTime)); };
  tick();
  _clockInterval = setInterval(tick, 1000);
}
function _stopClock() {
  if (_clockListeners.size > 0) return;
  clearInterval(_clockInterval);
  _clockInterval = null;
}

function Clock() {
  const [time, setTime] = useState(_clockTime);
  useEffect(() => {
    _clockListeners.add(setTime);
    _startClock();
    return () => { _clockListeners.delete(setTime); _stopClock(); };
  }, []);
  return time;
}

// Store token globally so all fetch calls can use it
let _authToken = localStorage.getItem('auth_token') || '';

// Patch global fetch to auto-attach auth token
const _origFetch = window.fetch;
window.fetch = function(url, opts = {}) {
  if (_authToken && typeof url === 'string' && url.includes('/api/') && !url.includes('/auth/login')) {
    opts.headers = { ...(opts.headers || {}), Authorization: `Bearer ${_authToken}` };
  }
  return _origFetch.call(this, url, opts).then(res => {
    // Auto-logout on 401
    if (res.status === 401 && !url.includes('/auth/')) {
      _authToken = '';
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.reload();
    }
    return res;
  });
};

function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('auth_token'));
  const [user, setUser] = useState(localStorage.getItem('auth_user') || '');
  const [fridayActive, setFridayActive] = useState(false);
  const [toast, setToast] = useState('');
  const chatRef = useRef(null);

  const toastTimerRef = useRef(null);
  const showToast = useCallback(msg => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => setToast(''), 3000);
  }, []);

  // Fix mobile 100vh — set CSS variable from actual viewport height
  useEffect(() => {
    const setVH = () => {
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${h * 0.01}px`);
    };
    setVH();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVH);
      return () => window.visualViewport.removeEventListener('resize', setVH);
    }
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  const handleLogin = useCallback((token, username) => {
    _authToken = token;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', username);
    setAuthed(true); setUser(username);
  }, []);

  const handleLogout = useCallback(() => {
    _authToken = '';
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_role');
    setAuthed(false); setUser('');
  }, []);

  // Verify token on mount
  useEffect(() => {
    if (!_authToken) return;
    const controller = new AbortController();
    fetch(`${API_URL}/auth/verify`, { signal: controller.signal }).then(r => r.json()).then(d => {
      if (!d.valid) { handleLogout(); }
    }).catch(() => {});
    return () => controller.abort();
  }, [handleLogout]);

  // Refresh counter — incremented when LLM or dashboard mutates data
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  // Cleanup toast timer on unmount
  useEffect(() => {
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); };
  }, []);

  // Initialize notifications after auth
  useEffect(() => {
    if (!authed) return;
    initNotifications().then(() => scheduleToday());
  }, [authed]);

  // Re-schedule when tasks change
  useEffect(() => {
    if (authed && refreshKey > 0) scheduleToday();
  }, [refreshKey, authed]);

  if (!authed) return <ErrorBoundary><Login onLogin={handleLogin} /></ErrorBoundary>;

  return (
    <ErrorBoundary>
    <div className="hq">
      {/* B13: Skip link */}
      <a href="#main-content" className="skip-link">SKIP TO CONTENT</a>
      {/* Background layers */}
      <div className="hq-grid" />
      <div className="hq-vignette" />

      {/* === HQ STATUS BAR === */}
      <header className="hq-bar">
        <div className="hq-bar-left">
          <div className="reactor-badge">
            <div className="reactor-core" />
            <svg className="reactor-ring" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /></svg>
          </div>
          <div className="hq-bar-brand">
            <span className="hq-bar-title">MASHRUR INDUSTRIES</span>
            <span className="hq-bar-version">COMMAND OS v3.7.2</span>
          </div>
          {/* Clock shows next to reactor on mobile when center is hidden */}
          <div className="hq-bar-clock-mobile">
            <span className="hq-bar-clock"><Clock /></span>
          </div>
        </div>
        <div className="hq-bar-center">
          <div className="hq-bar-divider" />
          <span className="hq-bar-clock"><Clock /></span>
          <span className="hq-bar-zone">UTC+6</span>
          <div className="hq-bar-divider" />
        </div>
        <div className="hq-bar-right">
          <div className="hq-bar-stat"><span className="hq-bar-dot hq-bar-dot-green" /><span>SYSTEMS</span></div>
          <div className="hq-bar-stat"><span className="hq-bar-dot hq-bar-dot-blue" /><span>AI LINK</span></div>
          <button className="hq-bar-notif" onClick={async () => {
            const perm = await requestNotificationPermission();
            if (perm === 'granted') { const n = await scheduleToday(); showToast(`Notifications enabled. ${n} task(s) scheduled.`); }
            else if (perm === 'native-prompt') { showToast('Please grant notification permission in the Android popup. Then tap this again.'); }
            else if (perm === 'unsupported') { showToast('Notifications not supported in this browser.'); }
            else showToast('Notification permission denied. Enable in browser/device settings.');
          }} aria-label="Toggle notifications" title="Enable notifications">🔔</button>
          <span className="hq-bar-user">{user}</span>
          <button className="hq-bar-logout" onClick={handleLogout} aria-label="Logout">⏻</button>
        </div>
      </header>

      {/* === MAIN WORKSPACE === */}
      <main id="main-content" className={`hq-workspace ${fridayActive ? 'hq-workspace-shifted' : ''}`}>
        {/* Left decorative data strip */}
        <div className="hq-deco-strip hq-deco-left">
          <div className="hq-deco-line" />
          <div className="hq-deco-pulse" />
          <div className="hq-deco-line" />
        </div>

        {/* Main panel */}
        <div className="hq-panel">
          {/* Animated background layers */}
          <div className="hq-anim-particles" />
          <div className="hq-anim-orbit" />
          {/* HUD corners */}
          <div className="hq-panel-corner hq-pc-tl" />
          <div className="hq-panel-corner hq-pc-tr" />
          <div className="hq-panel-corner hq-pc-bl" />
          <div className="hq-panel-corner hq-pc-br" />
          <Dashboard refreshKey={refreshKey} onDataChange={triggerRefresh} />
        </div>

        {/* Right decorative data strip */}
        <div className="hq-deco-strip hq-deco-right">
          <div className="hq-deco-line" />
          <div className="hq-deco-pulse hq-deco-pulse-delay" />
          <div className="hq-deco-line" />
        </div>
      </main>

      {/* === FRIDAY AI PANEL === */}
      <div className={`friday ${fridayActive ? 'friday-open' : ''}`} inert={!fridayActive}>
        <div className="fri-anim-particles" />
        <div className="fri-anim-wave" />
        <div className="friday-header">
          <div className="friday-header-left">
            <div className="friday-icon">
              <div className="friday-icon-core" />
              <svg className="friday-icon-ring" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" /></svg>
            </div>
            <div>
              <div className="friday-title">F.R.I.D.A.Y.</div>
              <div className="friday-subtitle">Neural Interface Online</div>
            </div>
          </div>
          <div className="friday-header-actions">
            <button className="friday-clear" onClick={() => chatRef.current?.clear()} title="Clear history" aria-label="Clear chat history">↺</button>
            <button className="friday-close" onClick={() => { setFridayActive(false); triggerRefresh(); }} aria-label="Close Friday panel">×</button>
          </div>
        </div>
        {localStorage.getItem('auth_role') === 'guest' && (
          <div className="guest-banner">👤 Guest Mode — Your data is isolated. Sign in for full access.</div>
        )}
        <div className="friday-body">
          <Chat ref={chatRef} onDataChange={triggerRefresh} active={fridayActive} />
        </div>
      </div>

      {/* === FRIDAY ACTIVATOR === */}
      <button className={`friday-activator ${fridayActive ? 'friday-activator-active' : ''}`} onClick={() => setFridayActive(p => !p)}
        aria-label={fridayActive ? 'Close AI Assistant' : 'Open AI Assistant'}>
        <div className="friday-activator-glow" />
        <div className="friday-activator-reactor">
          <div className="friday-activator-core" />
          <svg className="friday-activator-ring" viewBox="0 0 40 40"><circle cx="20" cy="20" r="16" /></svg>
          <svg className="friday-activator-ring2" viewBox="0 0 40 40"><circle cx="20" cy="20" r="13" /></svg>
        </div>
        <span className="friday-activator-label">{fridayActive ? '×' : 'AI'}</span>
      </button>

      {fridayActive && <div className="friday-backdrop" onClick={() => setFridayActive(false)} aria-hidden="true" />}

      <div aria-live="polite" role="status" className={toast ? 'hq-toast' : 'hq-toast-hidden'}>{toast}</div>
    </div>
    </ErrorBoundary>
  );
}

export default App;
